import router from '@/router';
import { isNetworkError } from '@/lib/isNetworkError';
import { useAccountsStore } from '@/stores/accounts';
import { useUserStore } from '@/stores/user';
import { RestError } from '@dabble/rest-client';

/**
 * Refresh the active user when an account/billing view mounts, tolerating an
 * expired session.
 *
 * The router guard authorises navigation from `accounts.init()`'s CACHED auth
 * result, so a session that expired (or was cleared) server-side *after* a
 * successful login is still treated as authed at navigation time. The live
 * `GET /auth/user` issued here is the first request to discover the dead
 * session, and it throws `RestError(401, "Authorization required")`.
 *
 * That 401 is an expected unauthenticated state, NOT a reportable error
 * (DABBLE-ACCOUNTS-2-S / 2-G). Settle into a clean logged-out state and send the
 * user to login instead of letting the rejection escape the mounted hook and
 * reach Vue's `errorHandler` → Sentry. Mirrors the expected-401 swallow in
 * `accounts.init()` (#97) and `loadAccounts()` (#92).
 *
 * A browser-level fetch failure (offline, dropped connection: "Load failed" /
 * "Failed to fetch" / "NetworkError…") is a skipped refresh, not a failure: the
 * user is already loaded (`accounts.init()` fetched it at boot), so keep that copy
 * and let the view carry on. Rethrowing it aborted the billing portal's mount
 * before it loaded billing, leaving the page empty, and reported the blip to
 * Sentry (DABBLE-ACCOUNTS-2-5V). The view's own requests still give feedback if
 * the connection stays down.
 *
 * @returns `true` when the user was refreshed (or kept after a network blip),
 *   `false` when the session had expired (the caller should skip any
 *   post-refresh work — the user is being redirected to login). Any other error
 *   still propagates so genuine failures stay visible and reported.
 */
export async function refreshCurrentUser(uid: string): Promise<boolean> {
  try {
    await useUserStore().fetchUser(uid);
    return true;
  } catch (err) {
    if (err instanceof RestError && err.code === 401) {
      await endExpiredSession();
      return false;
    }
    if (isNetworkError(err)) return true;
    throw err;
  }
}

/**
 * Settle a discovered-dead session and bounce to login. Shared by
 * {@link refreshCurrentUser} and `billing.fetchBillingOverview`: both discover an
 * expired session via a 401 on a live authed request and need the same response
 * — drop the local session and redirect to login, rather than leaving the user
 * on a broken page. The 401 itself is intentionally not reported to Sentry.
 */
export async function endExpiredSession(): Promise<void> {
  useAccountsStore().handleExpiredSession();
  await router.push({ name: 'login' });
}
